import { useTranslation } from 'react-i18next';
import { ArrowsClockwiseIcon, FolderIcon } from '@phosphor-icons/react';
import { FolderCard } from './FolderCard';
import { DocumentTable } from './DocumentTable';
import { OldVersionsTable } from './OldVersionsTable';
import { DocumentContentTab } from '../../constants';
import type { ContentTab } from './FolderHeader';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';

interface DocumentsFolderContentProps {
  activeTab: ContentTab;
  companyId: string;
  currentParentId: string | null;
  folders: DocumentNodeWithDetailsDto[];
  documents: DocumentNodeWithDetailsDto[];
  archivedDocuments: DocumentNodeWithDetailsDto[];
  isLoading: boolean;
  canRenameFolder: boolean;
  canDeleteFolder: boolean;
  canRenameDocument: boolean;
  canDeleteDocument: boolean;
  canDiffuseDocument: boolean;
  canSendForValidation: boolean;
  canValidateDocument: boolean;
  canUploadNewVersion: boolean;
  canArchiveDocument: boolean;
  loadingDocumentId?: string | null;
  highlightedNodeId?: string | null;
  onOpenFolder: (nodeId: string) => void;
  onOpenDocument: (nodeId: string) => void;
  onRename: (node: DocumentNodeWithDetailsDto) => void;
  onDelete: (node: DocumentNodeWithDetailsDto) => void;
  onDiffuse?: (doc: DocumentNodeWithDetailsDto) => void;
  onResend?: (doc: DocumentNodeWithDetailsDto) => void;
  onSendForValidation?: (doc: DocumentNodeWithDetailsDto) => void;
  onValidate?: (doc: DocumentNodeWithDetailsDto) => void;
  onReject?: (doc: DocumentNodeWithDetailsDto) => void;
  onUploadNewVersion?: (doc: DocumentNodeWithDetailsDto) => void;
  onArchive?: (doc: DocumentNodeWithDetailsDto) => void;
  onShowReaders?: (doc: DocumentNodeWithDetailsDto) => void;
}

export const DocumentsFolderContent = ({
  activeTab,
  companyId,
  currentParentId,
  folders,
  documents,
  archivedDocuments,
  isLoading,
  canRenameFolder,
  canDeleteFolder,
  canRenameDocument,
  canDeleteDocument,
  canDiffuseDocument,
  canSendForValidation,
  canValidateDocument,
  canUploadNewVersion,
  canArchiveDocument,
  loadingDocumentId,
  highlightedNodeId,
  onOpenFolder,
  onOpenDocument,
  onRename,
  onDelete,
  onDiffuse,
  onResend,
  onSendForValidation,
  onValidate,
  onReject,
  onUploadNewVersion,
  onArchive,
  onShowReaders,
}: DocumentsFolderContentProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin">
          <ArrowsClockwiseIcon size={32} className="text-primary" />
        </div>
      </div>
    );
  }

  if (activeTab === DocumentContentTab.FOLDERS) {
    return folders.length === 0 ? (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <FolderIcon size={64} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('documents.empty.folders_title')}
        </h3>
        <p className="text-gray-500 max-w-md">{t('documents.empty.folders_description')}</p>
      </div>
    ) : (
      <div className="pb-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
          {folders.map((node) => (
            <div key={node.id} className="flex-1 flex-shrink-0">
              <FolderCard
                node={node}
                childrenCount={node.childrenCount ?? 0}
                canRename={canRenameFolder}
                canDelete={canDeleteFolder}
                onOpen={onOpenFolder}
                onRename={onRename}
                onDelete={onDelete}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activeTab === DocumentContentTab.DOCUMENTS) {
    return documents.length === 0 ? (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <FolderIcon size={64} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('documents.empty.documents_title')}
        </h3>
        <p className="text-gray-500 max-w-md">{t('documents.empty.documents_description')}</p>
      </div>
    ) : (
      <DocumentTable
        documents={documents}
        canRename={canRenameDocument}
        canDelete={canDeleteDocument}
        canDiffuse={canDiffuseDocument}
        canSendForValidation={canSendForValidation}
        canValidate={canValidateDocument}
        canUploadNewVersion={canUploadNewVersion}
        canArchive={canArchiveDocument}
        loadingDocumentId={loadingDocumentId}
        highlightedNodeId={highlightedNodeId}
        onOpen={onOpenDocument}
        onRename={onRename}
        onDelete={onDelete}
        onDiffuse={onDiffuse}
        onResend={onResend}
        onSendForValidation={onSendForValidation}
        onValidate={onValidate}
        onReject={onReject}
        onUploadNewVersion={onUploadNewVersion}
        onArchive={onArchive}
        onShowReaders={onShowReaders}
      />
    );
  }

  if (activeTab === DocumentContentTab.ARCHIVED) {
    return archivedDocuments.length === 0 ? (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <FolderIcon size={64} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('documents.empty.documents_title')}
        </h3>
        <p className="text-gray-500 max-w-md">{t('documents.empty.documents_description')}</p>
      </div>
    ) : (
      <DocumentTable
        documents={archivedDocuments}
        canRename={false}
        canDelete={canDeleteDocument}
        canDiffuse={false}
        canSendForValidation={false}
        canValidate={false}
        canUploadNewVersion={false}
        canArchive={false}
        highlightedNodeId={highlightedNodeId}
        onOpen={onOpenDocument}
        onRename={onRename}
        onDelete={onDelete}
      />
    );
  }

  return <OldVersionsTable companyId={companyId} parentId={currentParentId} />;
};
